/**
 * Mobile Drawer Search Hook
 *
 * Manages search functionality for the mobile drawer including search mode state,
 * Algolia search integration, and search result handling.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAdvancedAlgoliaSearch } from "@/lib/hooks/algolia-search";
import { useSearchHistory } from "@wtc/ui";

interface UseMobileDrawerSearchProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  showFullPageLink?: boolean;
  fullPageSearchUrl?: string;
}

export function useMobileDrawerSearch({
  isOpen,
  onOpenChange,
  showFullPageLink = true,
  fullPageSearchUrl = "/search",
}: UseMobileDrawerSearchProps) {
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeTab, setActiveTab] = useState("search");
  const [localSearchQuery, setLocalSearchQuery] = useState("");

  // Initialize search hook but don't let it affect typing performance
  const {
    results: hookResults,
    isLoading: hookIsLoading,
    hasPersistedResults,
    searchAlgolia,
    handleResultClick,
    clearSearch,
    restoreSession,
    getHighlightedParts,
    getResultIcon,
  } = useAdvancedAlgoliaSearch();

  // Initialize search history hook
  const { history, removeFromHistory, clearHistory, getRecentHistory } = useSearchHistory();

  // Separate backend state from UI state
  const [backendResults, setBackendResults] = useState<any[]>([]);

  // Debounce search query - wait 300ms after user stops typing
  // This is completely separate from the UI input handling
  useEffect(() => {
    if (!isSearchMode || !localSearchQuery.trim()) {
      setBackendResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      // Perform the search asynchronously without blocking UI
      // No loading state during debounce - only show loading when API is actually called
      void searchAlgolia(localSearchQuery);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [localSearchQuery, isSearchMode, searchAlgolia]);

  // Sync backend results when the search hook updates
  useEffect(() => {
    setBackendResults(hookResults || []);
  }, [hookResults]);

  // Render highlighted text using data from hook (memoized to prevent re-renders)
  const renderHighlightedText = useCallback(
    (text: string) => {
      const parts = getHighlightedParts(text);

      return React.createElement(
        "span",
        null,
        parts.map((part: any, index: number) => {
          if (part.isHighlighted) {
            return React.createElement(
              "mark",
              { key: index, className: "bg-yellow-200 text-yellow-900 dark:bg-yellow-900/50 dark:text-yellow-200 px-1 rounded" },
              part.text
            );
          }
          return React.createElement("span", { key: index }, part.text);
        })
      );
    },
    [getHighlightedParts]
  );

  // Handle search functionality
  const handleSearchToggle = useCallback(() => {
    setIsSearchMode(!isSearchMode);
  }, [isSearchMode]);

  // Handle search input changes - completely UI focused, no backend dependency
  const handleSearchChange = useCallback(
    (value: string) => {
      // Update UI state immediately for smooth typing
      setLocalSearchQuery(value);

      // Clear results immediately if search is empty (no debounce needed)
      if (!value.trim()) {
        setBackendResults([]);
        clearSearch(); // Clear backend state too
      }
    },
    [clearSearch]
  );

  const handleSearchResultClick = useCallback(
    (result: any) => {
      handleResultClick(result, () => onOpenChange(false));
    },
    [handleResultClick, onOpenChange]
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchQuery("");
    setBackendResults([]);
    clearSearch();
  }, [clearSearch]);

  // History management functions
  const handleHistoryItemClick = useCallback((item: any) => {
    // Navigate to the URL
    window.location.href = item.url;
  }, []);

  const handleRemoveHistoryItem = useCallback(
    (itemId: string) => {
      removeFromHistory(itemId);
    },
    [removeFromHistory]
  );

  const handleClearHistory = useCallback(() => {
    if (confirm("Clear all search history?")) {
      clearHistory();
    }
  }, [clearHistory]);

  // Close drawer function
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Reset search mode and local query when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setIsSearchMode(false);
      setLocalSearchQuery("");
    }
  }, [isOpen]);

  // Reset local query when search mode is turned off
  useEffect(() => {
    if (!isSearchMode) {
      setLocalSearchQuery("");
    }
  }, [isSearchMode]);

  // Restore search session when entering search mode
  useEffect(() => {
    if (isSearchMode && !localSearchQuery && !backendResults.length) {
      restoreSession();
    }
  }, [isSearchMode, localSearchQuery, backendResults.length, restoreSession]);

  // Focus search input when entering search mode
  useEffect(() => {
    if (isSearchMode) {
      // Small delay to ensure the drawer has rendered
      setTimeout(() => {
        const searchInput = document.querySelector(
          "input[placeholder*=\"Search\"]"
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [isSearchMode]);

  return {
    isSearchMode,
    activeTab,
    setActiveTab,
    searchQuery: localSearchQuery, // Always use local query for smooth input display
    searchResults: backendResults, // Use separated backend results
    isSearchLoading: hookIsLoading, // Use the hook's loading state directly
    hasPersistedResults,
    renderHighlightedText,
    handleSearchToggle,
    handleSearchChange,
    handleSearchResultClick,
    handleClearSearch,
    getResultIcon,
    // History-related data and functions
    historyItems: getRecentHistory(20),
    handleHistoryItemClick,
    handleRemoveHistoryItem,
    handleClearHistory,
    // Full page search props
    showFullPageLink,
    fullPageSearchUrl,
    handleClose,
  };
}
